import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Message } from 'primereact/message';
import { lagerFinishedProductsAPI } from '../services/api';
import { LagerFinishedProduct, LagerUnit } from '../types';
import { showCustomAlert } from './CustomAlert';
import { useAuth } from '../contexts/AuthContext';

type ProductFormState = {
  name: string;
  unit: LagerUnit;
  perPallet?: number | null;
  m2PerPiece?: number | null;
};

const unitOptions: Array<{ label: string; value: LagerUnit }> = [
  { label: 'kom', value: 'kom' },
  { label: 'paleta', value: 'paleta' },
  { label: 'm²', value: 'm2' }
];

const LagerFinishedProducts: React.FC = () => {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'direktor' || user?.role === 'finansijski_gotovi';

  const [products, setProducts] = useState<LagerFinishedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editing, setEditing] = useState<LagerFinishedProduct | null>(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState<ProductFormState>({
    name: '',
    unit: 'kom',
    perPallet: null,
    m2PerPiece: null
  });

  const dialogHeader = useMemo(() => (editing ? 'Izmeni proizvod' : 'Dodaj proizvod'), [editing]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await lagerFinishedProductsAPI.getAll();
      setProducts(data);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri učitavanju proizvoda';
      setError(msg);
      showCustomAlert.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormData({ name: '', unit: 'kom', perPallet: null, m2PerPiece: null });
    setError('');
    setDialogVisible(true);
  };

  const openEdit = (product: LagerFinishedProduct) => {
    setEditing(product);
    setFormData({
      name: product.name || '',
      unit: product.unit || 'kom',
      perPallet: product.perPallet ?? null,
      m2PerPiece: product.m2PerPiece ?? null
    });
    setError('');
    setDialogVisible(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naziv je obavezan');
      return;
    }
    try {
      setError('');
      const payload: Partial<LagerFinishedProduct> = {
        name: formData.name.trim(),
        unit: formData.unit,
        perPallet: formData.perPallet ?? undefined,
        m2PerPiece: formData.m2PerPiece ?? undefined
      };
      if (editing?._id) {
        await lagerFinishedProductsAPI.update(editing._id, payload);
      } else {
        await lagerFinishedProductsAPI.create(payload);
      }
      setDialogVisible(false);
      await loadProducts();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Greška pri čuvanju proizvoda';
      setError(msg);
      showCustomAlert.error(msg);
    }
  };

  const handleDelete = (product: LagerFinishedProduct) => {
    showCustomAlert.confirm(
      `Da li ste sigurni da želite da obrišete proizvod "${product.name}"?`,
      async () => {
        try {
          await lagerFinishedProductsAPI.delete(product._id);
          await loadProducts();
        } catch (err: any) {
          const msg = err?.response?.data?.message || err?.message || 'Greška pri brisanju proizvoda';
          showCustomAlert.error(msg);
        }
      },
      'Potvrda brisanja'
    );
  };

  const unitBody = (row: LagerFinishedProduct) => (row.unit === 'm2' ? 'm²' : row.unit);

  const m2PerPalletBody = (row: LagerFinishedProduct) => {
    const perPallet = row.perPallet || 0;
    const m2PerPiece = row.m2PerPiece || 0;
    const total = perPallet * m2PerPiece;
    return total ? total.toFixed(2) : '';
  };

  const actionsBodyTemplate = (rowData: LagerFinishedProduct) => {
    if (!canManage) return null;
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-pencil"
          className="action-icon-btn"
          text
          rounded
          onClick={() => openEdit(rowData)}
          tooltip="Izmeni"
        />
        <Button
          icon="pi pi-trash"
          className="action-icon-btn"
          text
          rounded
          severity="danger"
          onClick={() => handleDelete(rowData)}
          tooltip="Obriši"
        />
      </div>
    );
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      <Card title="Proizvodi">
        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="flex justify-content-end gap-2" style={{ marginBottom: '1rem' }}>
          {canManage && <Button label="Dodaj" icon="pi pi-plus" onClick={openCreate} />}
          <Button label="Osveži" icon="pi pi-refresh" severity="secondary" outlined onClick={loadProducts} />
        </div>

        <DataTable value={products} loading={loading} paginator rows={10} rowsPerPageOptions={[10, 25, 50]} responsiveLayout="scroll">
          <Column field="name" header="Naziv" sortable />
          <Column header="Jedinica" body={unitBody} sortable sortField="unit" />
          <Column field="perPallet" header="Kom/pal" />
          <Column header="m²/kom" body={(r: LagerFinishedProduct) => (r.m2PerPiece ? r.m2PerPiece.toFixed(2) : '')} />
          <Column header="m²/pal" body={m2PerPalletBody} />
          {canManage && <Column header="Akcije" body={actionsBodyTemplate} style={{ width: '140px' }} />}
        </DataTable>
      </Card>

      <Dialog
        header={dialogHeader}
        visible={dialogVisible}
        style={{ width: '680px', maxWidth: '96vw' }}
        onHide={() => setDialogVisible(false)}
        modal
        closable
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12">
              <label htmlFor="name" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Naziv
              </label>
              <InputText
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                placeholder="Unesite naziv proizvoda"
              />
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="unit" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Jedinica
              </label>
              <Dropdown
                id="unit"
                value={formData.unit}
                options={unitOptions}
                optionLabel="label"
                optionValue="value"
                onChange={(e) => setFormData((p) => ({ ...p, unit: e.value }))}
                appendTo="self"
              />
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="perPallet" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                Kom/paleta (fiksno)
              </label>
              <InputNumber
                id="perPallet"
                value={formData.perPallet ?? null}
                onValueChange={(e) => setFormData((p) => ({ ...p, perPallet: e.value ?? null }))}
                min={0}
                mode="decimal"
                useGrouping={false}
              />
            </div>

            <div className="col-12 md:col-6">
              <label htmlFor="m2PerPiece" style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>
                m²/kom (rolna)
              </label>
              <InputNumber
                id="m2PerPiece"
                value={formData.m2PerPiece ?? null}
                onValueChange={(e) => setFormData((p) => ({ ...p, m2PerPiece: e.value ?? null }))}
                min={0}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={6}
                useGrouping={false}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-content-end" style={{ marginTop: '1.25rem' }}>
            <Button label="Sačuvaj" icon="pi pi-check" onClick={handleSubmit} />
            <Button label="Otkaži" icon="pi pi-times" severity="secondary" outlined onClick={() => setDialogVisible(false)} />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default LagerFinishedProducts;


